import React, { useEffect, useState } from "react";
import { Link, useNavigate, useOutletContext } from "react-router-dom";
import "../../../components/projects/Projects.css";
import "../../shared/SubscriptionPage.css";
import api from "../../../api/client";
import "../../../components/users/UserActivityReportModal.css";
import { formatProjectStatus } from "../../../utils/formatProjectStatus";
import TeamTaskAnalytics from "../../../components/tasks/TeamTaskAnalytics";
import { PERMISSIONS } from "../../../constants/permissions";
import PermissionGate from "../../../components/common/PermissionGate";

function formatPercent(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "0%";
  return `${numeric.toFixed(1)}%`;
}

function formatDays(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "-";
  return `${numeric.toFixed(1)} days`;
}

function formatOverdue(expectedEndDate) {
  if (!expectedEndDate) return null;
  const due = new Date(expectedEndDate);
  if (Number.isNaN(due.getTime())) return null;
  const minutes = Math.floor((Date.now() - due.getTime()) / 60000);
  if (minutes <= 0) return null;
  if (minutes < 60) return `${minutes}m overdue`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${hours}h overdue`;
  const days = Math.floor(hours / 24);
  return `${days}d overdue`;
}

function formatMinutesAsHours(value) {
  const numeric = Number(value);
  if (!Number.isFinite(numeric) || numeric <= 0) return "0h";
  const hours = numeric / 60;
  return `${hours.toFixed(1)}h`;
}

export default function DashboardPage() {
  const navigate = useNavigate();
  const { subStatus } = useOutletContext() || {};
  const [data, setData] = useState(null);
  const [error, setError] = useState("");
  const [alertFilter, setAlertFilter] = useState("all");

  const buildAlerts = (alerts = []) => alerts;

  useEffect(() => {
    const load = async () => {
      try {
        const resp = await api.get("/projects/overview");
        setData(resp.data || null);
      } catch (err) {
        setError(err.response?.data?.message || "Failed to load overview");
      }
    };
    load();
  }, []);

  const subBannerText = () => {
    if (
      !subStatus ||
      !subStatus.warningLevel ||
      subStatus.warningLevel === "expired"
    )
      return null;
    const days = subStatus.daysRemaining;
    const planLabel = subStatus.planType === "TRIAL" ? "Trial" : "Pro";
    if (subStatus.warningLevel === "critical")
      return days !== null
        ? `Your ${planLabel} plan expires in ${days} day(s) renew immediately!`
        : `Your ${planLabel} plan is expiring soon — renew immediately!`;
    if (subStatus.warningLevel === "warning")
      return days !== null
        ? `Your ${planLabel} plan expires in ${days} day(s). Renew now to avoid interruption.`
        : `Your ${planLabel} plan is expiring soon. Renew now to avoid interruption.`;
    return null;
  };

  return (
    <div className="dashboard-page">
      {subBannerText() && (
        <div
          className={`sub-banner ${subStatus.warningLevel}`}
          style={{ margin: "0 0 8px" }}
        >
          <span>{subBannerText()}</span>
          <button
            type="button"
            className="sub-banner-action"
            onClick={() => navigate("/subscription")}
          >
            Manage Subscription
          </button>
        </div>
      )}
      <div className="page-header">
        <h1 style={{ margin: 0 }}>Overview</h1>
      </div>
      {data && (
        <>
          {data.kpis && (
            <div className="panel dashboard-kpi-panel">
              <div className="page-header">
                <h3 style={{ margin: 0 }}>Founder KPIs</h3>
              </div>
              <div className="dashboard-kpi-grid">
                <div className="dashboard-kpi-card">
                  <div className="dashboard-kpi-label">Completion Rate</div>
                  <div className="dashboard-kpi-value">
                    {formatPercent(
                      data.kpis.completionBreakdown?.completionRate,
                    )}
                  </div>
                  <div className="dashboard-kpi-sub">
                    {data.kpis.completionBreakdown?.completed || 0} of{" "}
                    {data.kpis.completionBreakdown?.total || 0} completed
                  </div>
                </div>

                <div className="dashboard-kpi-card">
                  <div className="dashboard-kpi-label">Active Projects</div>
                  <div className="dashboard-kpi-value">
                    {data.kpis.activeProjects?.count || 0}
                  </div>
                  <div className="dashboard-kpi-sub">
                    In Progress: {data.kpis.activeProjects?.inProgress || 0}
                  </div>
                </div>

                <div className="dashboard-kpi-card">
                  <div className="dashboard-kpi-label">Avg Cycle Time</div>
                  <div className="dashboard-kpi-value">
                    {formatDays(data.kpis.cycleTime?.averageDays)}
                  </div>
                  <div className="dashboard-kpi-sub">
                    Sample: {data.kpis.cycleTime?.sampleSize || 0} completed
                    projects
                  </div>
                </div>

                <div className="dashboard-kpi-card">
                  <div className="dashboard-kpi-label">Rework Rate (30d)</div>
                  <div className="dashboard-kpi-value">
                    {formatPercent(data.kpis.reworkRate?.percent)}
                  </div>
                  <div className="dashboard-kpi-sub">
                    {data.kpis.reworkRate?.reworkCount || 0} of{" "}
                    {data.kpis.reworkRate?.totalReviewed || 0} reviews
                  </div>
                </div>

                <div className="dashboard-kpi-card">
                  <div className="dashboard-kpi-label">
                    Team Utilization (7d)
                  </div>
                  <div className="dashboard-kpi-value">
                    {formatPercent(data.kpis.utilization?.utilizationPercent)}
                  </div>
                  <div className="dashboard-kpi-sub">
                    {formatMinutesAsHours(data.kpis.utilization?.loggedMinutes)}{" "}
                    /{" "}
                    {formatMinutesAsHours(
                      data.kpis.utilization?.capacityMinutes,
                    )}
                  </div>
                </div>

                <div className="dashboard-kpi-card">
                  <div className="dashboard-kpi-label">Overdue Work</div>
                  <div className="dashboard-kpi-value">
                    {data.kpis.overdueWork?.count || 0}
                  </div>
                  <div className="dashboard-kpi-sub">
                    Assignments past expected end date
                  </div>
                </div>
              </div>

              <div className="dashboard-kpi-details">
                <div className="dashboard-kpi-detail-card">
                  <div className="analytics-title">Completion Breakdown</div>
                  <div className="analytics-bar">
                    <div
                      className="analytics-seg pending"
                      style={{
                        width: `${data.kpis.completionBreakdown?.pendingPercent || 0}%`,
                      }}
                      title="Pending"
                    />
                    <div
                      className="analytics-seg progress"
                      style={{
                        width: `${data.kpis.completionBreakdown?.inProgressPercent || 0}%`,
                      }}
                      title="In Progress"
                    />
                    <div
                      className="analytics-seg complete"
                      style={{
                        width: `${data.kpis.completionBreakdown?.completedPercent || 0}%`,
                      }}
                      title="Completed"
                    />
                    <div
                      className="analytics-seg hold"
                      style={{
                        width: `${data.kpis.completionBreakdown?.onHoldPercent || 0}%`,
                      }}
                      title="On Hold"
                    />
                    <div
                      className="analytics-seg awaiting-invoice"
                      style={{
                        width: `${data.kpis.completionBreakdown?.awaitingInvoicePercent || 0}%`,
                      }}
                      title="Awaiting Invoice"
                    />
                  </div>
                  <div className="analytics-legend">
                    <span>
                      <i className="dot pending" /> Pending (
                      {data.kpis.completionBreakdown?.pending || 0})
                    </span>
                    <span>
                      <i className="dot progress" /> In Progress (
                      {data.kpis.completionBreakdown?.inProgress || 0})
                    </span>
                    <span>
                      <i className="dot complete" /> Completed (
                      {data.kpis.completionBreakdown?.completed || 0})
                    </span>
                    <span>
                      <i className="dot hold" /> On Hold (
                      {data.kpis.completionBreakdown?.onHold || 0})
                    </span>
                    <span>
                      <i className="dot awaiting-invoice" /> Awaiting Invoice (
                      {data.kpis.completionBreakdown?.awaitingInvoice || 0})
                    </span>
                  </div>
                </div>

                <div className="dashboard-kpi-detail-card">
                  <div className="analytics-title">Overdue Sample</div>
                  {!(data.kpis.overdueWork?.sample || []).length && (
                    <div className="small-muted">
                      No overdue stage assignments.
                    </div>
                  )}
                  {(data.kpis.overdueWork?.sample || [])
                    .slice(0, 4)
                    .map((item) => (
                      <div key={item.id} className="dashboard-overdue-row">
                        <div className="dashboard-overdue-main">
                          <strong>{item.project?.name || "Project"}</strong>
                          <div className="small-muted">
                            {item.stageName} - {item.assignee?.firstName}{" "}
                            {item.assignee?.lastName}
                          </div>
                        </div>
                        {formatOverdue(item.expectedEndDate) && (
                          <span
                            className="dashboard-overdue-badge"
                            title={`Expected end ${new Date(
                              item.expectedEndDate,
                            ).toLocaleDateString()}`}
                          >
                            {formatOverdue(item.expectedEndDate)}
                          </span>
                        )}
                      </div>
                    ))}
                </div>
              </div>
            </div>
          )}

          <div className="dashboard-stats">
            <div className="stat-card">
              <div
                className="small-muted"
                onClick={() => navigate("/projects")}
                style={{ cursor: "pointer" }}
              >
                Projects
              </div>
              <h2>{data.stats.projects}</h2>
            </div>
            <div className="stat-card">
              <div
                className="small-muted"
                onClick={() => navigate("/projects")}
                style={{ cursor: "pointer" }}
              >
                In Progress
              </div>
              <h2>{data.stats.inProgress}</h2>
            </div>
            <div className="stat-card">
              <div
                className="small-muted"
                onClick={() => navigate("/projects")}
                style={{ cursor: "pointer" }}
              >
                Completed
              </div>
              <h2>{data.stats.completed}</h2>
            </div>
            <div
              className="stat-card"
              onClick={() => navigate("/admin/users")}
              style={{ cursor: "pointer" }}
            >
              <div className="small-muted">Users</div>
              <h2>{data.stats.users}</h2>
            </div>
            <div className="stat-card">
              <div
                className="small-muted"
                onClick={() => navigate("/admin/workflows")}
                style={{ cursor: "pointer" }}
              >
                Active Workflows
              </div>
              <h2>{data.stats.activeWorkflows}</h2>
            </div>
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <div className="page-header">
              <h3 style={{ margin: 0 }}>Project Analytics</h3>
            </div>
            <div className="dashboard-analytics">
              <div className="analytics-card">
                <div className="analytics-title">Status Breakdown</div>
                <div className="analytics-bar">
                  <div
                    className="analytics-seg pending"
                    style={{
                      width: `${Math.round(
                        data.stats.projects
                          ? ((data.stats.projects -
                              data.stats.inProgress -
                              data.stats.completed) /
                              data.stats.projects) *
                              100
                          : 0,
                      )}%`,
                    }}
                    title="Pending"
                  />
                  <div
                    className="analytics-seg progress"
                    style={{
                      width: `${Math.round(
                        data.stats.projects
                          ? (data.stats.inProgress / data.stats.projects) * 100
                          : 0,
                      )}%`,
                    }}
                    title="In Progress"
                  />
                  <div
                    className="analytics-seg complete"
                    style={{
                      width: `${Math.round(
                        data.stats.projects
                          ? (data.stats.completed / data.stats.projects) * 100
                          : 0,
                      )}%`,
                    }}
                    title="Completed"
                  />
                </div>
                <div className="analytics-legend">
                  <span>
                    <i className="dot pending" /> Pending
                  </span>
                  <span>
                    <i className="dot progress" /> In Progress
                  </span>
                  <span>
                    <i className="dot complete" /> Completed
                  </span>
                </div>
              </div>

              <div className="analytics-card">
                <div className="analytics-title">Alerts Snapshot</div>
                <div className="analytics-metric">
                  <strong>{(data.alerts || []).length}</strong>
                  <span>Active Alerts</span>
                </div>
                <div className="analytics-sub">
                  {data.alerts && data.alerts.length > 0
                    ? `Highest: ${data.alerts[0].percent}%`
                    : "No alerts"}
                </div>
              </div>
            </div>
          </div>

          <PermissionGate permission={PERMISSIONS.TASK_VIEW_ALL}>
            <div className="panel" style={{ marginTop: 16 }}>
              <div className="page-header">
                <h3 style={{ margin: 0 }}>Task Analytics</h3>
                <Link className="btn-secondary" to="/tasks">
                  My Tasks
                </Link>
              </div>
              <TeamTaskAnalytics />
            </div>
          </PermissionGate>

          <div className="panel" style={{ marginTop: 16 }}>
            <div className="page-header">
              <h3 style={{ margin: 0 }}>Recent Projects</h3>
              <Link className="btn-secondary" to="/projects">
                View All
              </Link>
            </div>
            {(data.recentProjects || []).map((project) => (
              <div key={project.id} className="page-header">
                <div>
                  <strong>{project.name}</strong>
                  <div className="small-muted">
                    {project.currentStageName || "No active stage"}
                  </div>
                </div>
                <div>
                  <span className={`status-badge status-${project.status}`}>
                    {formatProjectStatus(project.status)}
                  </span>
                </div>
              </div>
            ))}
          </div>

          <div className="panel" style={{ marginTop: 16 }}>
            <div className="page-header">
              <h3 style={{ margin: 0 }}>Alerts</h3>
              <div style={{ display: "flex", gap: 8 }}>
                {[
                  { key: "all", label: "All" },
                  { key: "project", label: "Overdue" },
                  { key: "stage", label: "Stage Overdue" },
                ].map(({ key, label }) => (
                  <button
                    key={key}
                    type="button"
                    className={
                      alertFilter === key ? "btn-primary" : "btn-secondary"
                    }
                    style={{ padding: "4px 12px", fontSize: 13 }}
                    onClick={() => setAlertFilter(key)}
                  >
                    {label}
                  </button>
                ))}
              </div>
            </div>
            {buildAlerts(
              (data.alerts || []).filter((a) =>
                alertFilter === "all"
                  ? true
                  : (a.scope || "project") === alertFilter,
              ),
            ).length === 0 && <div className="small-muted">No alerts.</div>}
            {buildAlerts(
              (data.alerts || []).filter((a) =>
                alertFilter === "all"
                  ? true
                  : (a.scope || "project") === alertFilter,
              ),
            ).map((project) => (
              <div
                key={`${project.id}-${project.scope || "project"}`}
                className="page-header"
              >
                <div>
                  <strong>{project.name}</strong>
                  <div className="small-muted">
                    {project.scope === "stage" && project.stageName
                      ? `Stage: ${project.stageName} - `
                      : ""}
                    {project.durationValue} {project.durationUnit} -{" "}
                    {project.percent}% elapsed
                  </div>
                  {project.scope === "stage" && project.assignee && (
                    <div className="small-muted">
                      Assigned to: {project.assignee.firstName}{" "}
                      {project.assignee.lastName}
                    </div>
                  )}
                </div>
                <div>
                  <span
                    className={`status-badge ${
                      project.level === "overdue"
                        ? "status-ON_HOLD"
                        : "status-PENDING"
                    }`}
                  >
                    {project.scope === "stage"
                      ? project.level === "overdue"
                        ? "Stage Overdue"
                        : "Stage Warning"
                      : project.level === "overdue"
                        ? "Overdue"
                        : "80% Warning"}
                  </span>
                </div>
              </div>
            ))}
          </div>
        </>
      )}
      {!data && <div className="small-muted">{error || "Loading..."}</div>}

    </div>
  );
}

